import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Check } from 'lucide-react';
import { useOnboardingRedirect } from '../hooks/useOnboardingRedirect';
import { useFlowProtection, setFlowData } from '../../hooks/useFlowProtection';

const BackgroundGrid = () => (
    <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden bg-[#F8F9FB]">
        {/* Ellipse 1: Main Gradient Background */}
        <div
            className="absolute"
            style={{
                width: '2618.47px',
                height: '1939.34px',
                left: '-448.14px',
                top: '-919.31px',
                background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.8) 0%, rgba(116, 153, 191, 0.465385) 41.83%, rgba(237, 242, 247, 0) 100%)',
                transform: 'rotate(26.84deg)',
                transformOrigin: 'top left',
            }}
        />

        {/* Ellipse 2827: Blurred Overlay */}
        <div
            className="absolute hidden md:block"
            style={{
                width: '640px',
                height: '458px',
                left: '50%',
                top: '282px',
                transform: 'translateX(-50%) matrix(1, 0, 0, -1, 0, 0)',
                background: 'rgba(255, 255, 255, 0.4)',
                filter: 'blur(93.2px)',
                borderRadius: '53px',
            }}
        />

        {/* Grid Lines */}
        <div className="absolute inset-0 flex justify-center pointer-events-none">
            <div className="w-full h-full max-w-[1920px] relative">
                {/* Vertical Lines */}
                {[...Array(12)].map((_, i) => (
                    <div
                        key={`v-${i}`}
                        className="absolute top-0 bottom-0 border-l border-dashed border-[rgba(40,40,42,0.2)]"
                        style={{ left: `${105 + (i * 120)}px` }}
                    />
                ))}

                {/* Horizontal Lines */}
                {[121, 300, 449, 627, 700, 872, 913, 1031].map((top, i) => (
                    <div
                        key={`h-${i}`}
                        className="absolute left-0 right-0 border-t border-dashed border-[rgba(40,40,42,0.2)]"
                        style={{ top: `${top}px` }}
                    />
                ))}
            </div>
        </div>
    </div>
);

const Stepper = ({ currentStep }: { currentStep: string }) => {
    const steps = [
        { label: 'Upload your resume', id: 'upload' },
        { label: 'Resume Review & Edit', id: 'review' },
        { label: 'Career Goals & Aspirations', id: 'goals' }
    ];

    const getStatus = (stepId: string) => {
        if (currentStep === 'success') {
            if (stepId === 'upload') return 'active';
            return 'upcoming';
        }
        return 'upcoming';
    };

    return (
        <div className="flex items-center justify-center w-full max-w-3xl mx-auto mb-12 relative z-10 px-4">
            {steps.map((step, idx) => {
                const status = getStatus(step.id);
                const isActive = status === 'active';

                return (
                    <div key={idx} className="flex flex-1 items-center last:flex-none">
                        <div className="flex flex-col items-center relative">
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center border-[3px] z-10 transition-colors duration-300 ${
                                isActive
                                    ? 'bg-[#8F9FB8] border-[#CBD5E1]'
                                    : 'bg-white border-[#CBD5E1]'
                            }`}>
                                {isActive && <div className="w-2 h-2 bg-white rounded-full" />}
                            </div>
                            <span className={`absolute top-8 text-xs sm:text-sm font-medium whitespace-nowrap transition-colors duration-300 ${
                                isActive ? 'text-[#015FA6] font-bold' : 'text-[#015FA6]/70'
                            }`}>
                                {step.label}
                            </span>
                        </div>
                        {idx !== steps.length - 1 && (
                            <div className={`flex-1 h-[2px] mx-2 transition-colors duration-300 bg-[#CBD5E1]`} />
                        )}
                    </div>
                );
            })}
        </div>
    );
};

const ResumeConfirmation: React.FC = () => {
    const navigate = useNavigate();

    // Flow protection - requires coming from resume upload
    const { isValidAccess } = useFlowProtection({
        storageKey: 'fromResumeUpload',
        allowIncompleteProfile: true,
        fallbackPath: '/candidate/dashboard'
    });

    // Redirect users who have already completed onboarding
    useOnboardingRedirect();

    // Auto-redirect after 3 seconds
    useEffect(() => {
        if (isValidAccess !== true) return;

        const timer = setTimeout(() => {
            // Set flow data for next page
            setFlowData('fromResumeConfirmation', { confirmed: true });
            navigate('/welcome/profile-review');
        }, 3000);

        return () => clearTimeout(timer);
    }, [navigate, isValidAccess]);

    // Don't render until flow access is validated
    if (isValidAccess === null) {
        return null;
    }

    return (
        <div className="min-h-screen bg-[#F8F9FB] font-manrope relative flex flex-col overflow-hidden">
            <BackgroundGrid />

            {/* Header */}
            <header className="relative z-10 pt-8 pb-4 text-center">
                <div className="mb-6">
                    <img
                        src="/branding/logo_blue.svg"
                        alt="Hush"
                        style={{
                            width: '100px',
                            height: '46px',
                            objectFit: 'contain',
                            margin: '0 auto'
                        }}
                    />
                </div>
                <Stepper currentStep="success" />
            </header>

            {/* Main Content */}
            <main className="relative z-10 flex-1 px-4 md:px-8 py-4 max-w-[1400px] mx-auto w-full flex flex-col">
                <div className="flex flex-col items-center w-full max-w-4xl mx-auto animate-fade-in px-4 text-center">
                    {/* 4xl Header Typography */}
                    <h2 className="text-[#015FA6] font-bold text-[36px] md:text-[48px] leading-[100%] mb-4">
                        We got your resume successfully!
                    </h2>
                    {/* Subtitle Typography */}
                    <p className="text-[#015FA6] font-extralight text-[20px] md:text-[24px] leading-[150%] mb-12">
                        Thanks! We've got your resume. Let's review what we found.
                    </p>

                    {/* Abstract Illustration Representation */}
                    <div className="relative w-64 h-64 mb-12 flex items-center justify-center">
                        {/* Background shapes */}
                        <div className="absolute top-0 right-0 w-32 h-32 bg-[#FFD700] rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob"></div>
                        <div className="absolute top-0 left-0 w-32 h-32 bg-[#015FA6] rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-2000"></div>
                        <div className="absolute -bottom-8 left-20 w-32 h-32 bg-pink-300 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-4000"></div>

                        {/* Main Icon */}
                        <div className="relative z-10 bg-white p-6 rounded-3xl shadow-lg border border-gray-100">
                            <FileText size={80} className="text-[#015FA6]" />
                            <div className="absolute -bottom-2 -right-2 bg-green-500 text-white p-2 rounded-full shadow-lg border-4 border-white">
                                <Check size={24} strokeWidth={4} />
                            </div>
                        </div>
                    </div>

                    <button
                        onClick={() => {
                            setFlowData('fromResumeConfirmation', { confirmed: true });
                            navigate('/welcome/profile-review');
                        }}
                        className="px-12 py-3 bg-[#015FA6] text-white rounded-lg font-bold hover:bg-[#014f8c] transition-all shadow-lg shadow-blue-900/20 hover:scale-105"
                    >
                        Continue
                    </button>
                </div>
            </main>

            <style>{`
                @keyframes fade-in {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                .animate-fade-in {
                    animation: fade-in 0.5s ease-out forwards;
                }

                @keyframes blob {
                    0% { transform: translate(0px, 0px) scale(1); }
                    33% { transform: translate(30px, -50px) scale(1.1); }
                    66% { transform: translate(-20px, 20px) scale(0.9); }
                    100% { transform: translate(0px, 0px) scale(1); }
                }
                .animate-blob {
                    animation: blob 7s infinite;
                }
                .animation-delay-2000 {
                    animation-delay: 2s;
                }
                .animation-delay-4000 {
                    animation-delay: 4s;
                }
            `}</style>
        </div>
    );
};

export default ResumeConfirmation;
